import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEvidenceChainStore } from "../store/evidenceChainStore";
import { useAIContext } from "../store/aiContextStore";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorBanner from "../components/common/ErrorBanner";

/**
 * EU Evidence Chain (v1)
 * 
 * UI for managing evidence chain items, integrity scoring,
 * and admissibility levels for cross-border judicial compatibility.
 */
export default function EvidenceChain() {
  const { t } = useTranslation();
  const {
    evidenceItems,
    chainLength,
    chainIntegrityScore,
    admissibilityLevel,
    chainJitterMS,
    chainDriftMinutes,
    addEvidence,
    removeEvidence,
    purgeLowSeverity,
    exportEvidenceChain,
  } = useEvidenceChainStore();

  // Input state
  const [hash, setHash] = useState("");
  const [producer, setProducer] = useState("");
  const [region, setRegion] = useState("");
  const [severity, setSeverity] = useState(1);
  const [error, setError] = useState<Error | null>(null);

  // Derived values - must be defined before useEffect that uses them
  const hashEntropy = useEvidenceChainStore((s) => s.getHashEntropy());
  const chainVolatility = useEvidenceChainStore((s) => s.getChainVolatility());

  // Set active panel for AI context
  useEffect(() => {
    useAIContext.getState().setActivePanel("EvidenceChainPanel");
  }, []);

  // Update AI context inputs on change
  useEffect(() => {
    useAIContext.getState().setInput("chainLength", chainLength);
    useAIContext.getState().setInput("chainIntegrityScore", chainIntegrityScore);
    useAIContext.getState().setInput("admissibilityLevel", admissibilityLevel);
    useAIContext.getState().setInput("chainVolatility", chainVolatility);
    useAIContext.getState().setInput("chainJitterMS", chainJitterMS);
    useAIContext.getState().setInput("chainDriftMinutes", chainDriftMinutes);
  }, [chainLength, chainIntegrityScore, admissibilityLevel, chainVolatility, chainJitterMS, chainDriftMinutes]);

  const handleAddEvidence = () => {
    if (!hash || !producer || !region) {
      setError(new Error(t("evidenceChain.invalidInput") || "Hash, producer, and region are required"));
      return;
    }
    
    setError(null);
    try {
      addEvidence(hash, producer, region, severity);
      setHash("");
      setProducer("");
      setRegion("");
      setSeverity(1);
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("evidenceChain.addFailed") || "Failed to add evidence");
      setError(error);
      console.error("Add evidence failed:", e);
    }
  };

  const handleRemove = (id: string) => {
    setError(null);
    try {
      removeEvidence(id);
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("evidenceChain.removeFailed") || "Failed to remove evidence");
      setError(error);
      console.error("Remove evidence failed:", e);
    }
  };

  const handlePurge = () => {
    setError(null);
    try {
      purgeLowSeverity();
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("evidenceChain.purgeFailed") || "Failed to purge evidence");
      setError(error);
      console.error("Purge failed:", e);
    }
  };

  const handleExport = () => {
    if (evidenceItems.length === 0) {
      setError(new Error(t("evidenceChain.noItems") || "No evidence items to export"));
      return;
    }
    
    setError(null);
    try {
      exportEvidenceChain();
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("evidenceChain.exportFailed") || "Failed to export evidence chain");
      setError(error);
      console.error("Export failed:", e);
    }
  };

  return (
    <div className="space-y-6">
      <div className="font-orbitron text-evat-neon text-xl mb-4">
        {t("evidenceChain.title") || "EU Evidence Chain"}
      </div>

      <ErrorBanner error={error} onRetry={() => setError(null)} showRetry={!!error} />

      {/* Input Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("evidenceChain.hash") || "Hash"}
          </label>
          <input
            type="text"
            value={hash}
            onChange={(e) => setHash(e.target.value)}
            placeholder="e.g., 0x1234..."
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("evidenceChain.producer") || "Producer"}
          </label>
          <input
            type="text"
            value={producer}
            onChange={(e) => setProducer(e.target.value)}
            placeholder="e.g., DE-Authority-001"
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("evidenceChain.region") || "Region"}
          </label>
          <input
            type="text"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            placeholder="e.g., EU, EEA, National"
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("evidenceChain.severity") || "Severity (1-10)"}
          </label>
          <input
            type="number"
            min="1"
            max="10"
            value={severity}
            onChange={(e) => setSeverity(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={handleAddEvidence}
          disabled={!hash || !producer || !region}
          className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t("evidenceChain.add") || "Add Evidence"}
        </button>
        <button
          onClick={handlePurge}
          disabled={evidenceItems.length === 0}
          className="px-4 py-2 bg-red-500/20 border border-red-500 text-red-400 rounded hover:bg-red-500/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t("evidenceChain.purge") || "Purge Low Severity"}
        </button>
      </div>

      {/* Evidence Items List */}
      {evidenceItems.length > 0 && (
        <div className="space-y-2 mt-6">
          <div className="font-orbitron text-evat-gold text-sm mb-2">
            {t("evidenceChain.evidenceItems") || "Evidence Items"} ({chainLength})
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {evidenceItems.map((item) => (
              <div
                key={item.id}
                className="border border-evat-gold/30 rounded p-3 bg-black/50 flex justify-between items-center"
              >
                <div className="flex-1">
                  <div className="text-xs text-neutral-400 mb-1">
                    {item.producer} • {item.region} • Severity: {item.severity}
                  </div>
                  <div className="font-mono text-xs text-evat-neon break-all">
                    {item.hash.substring(0, 32)}...
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">
                    {new Date(item.timestampISO).toLocaleString()}
                  </div>
                </div>
                <button
                  onClick={() => handleRemove(item.id)}
                  className="ml-4 px-2 py-1 text-xs bg-red-500/20 border border-red-500/40 text-red-400 rounded hover:bg-red-500/30 transition-colors"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metrics Display */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("evidenceChain.chainLength") || "Chain Length"}
          </div>
          <div className="text-lg font-orbitron text-evat-gold">
            {chainLength}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("evidenceChain.chainIntegrityScore") || "Integrity Score"}
          </div>
          <div className={`text-lg font-orbitron ${
            chainIntegrityScore >= 80 ? "text-green-400" :
            chainIntegrityScore >= 50 ? "text-yellow-400" :
            "text-red-400"
          }`}>
            {chainIntegrityScore.toFixed(1)}%
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("evidenceChain.admissibilityLevel") || "Admissibility"}
          </div>
          <div className={`text-lg font-orbitron ${
            admissibilityLevel === "high" ? "text-green-400" :
            admissibilityLevel === "medium" ? "text-yellow-400" :
            "text-red-400"
          }`}>
            {admissibilityLevel.toUpperCase()}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("evidenceChain.chainVolatility") || "Chain Volatility"}
          </div>
          <div className="text-sm font-orbitron text-orange-400">
            {chainVolatility.toFixed(2)}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("evidenceChain.chainJitterMS") || "Chain Jitter"}
          </div>
          <div className="text-sm font-orbitron text-purple-400">
            {chainJitterMS}ms
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("evidenceChain.chainDriftMinutes") || "Chain Drift"}
          </div>
          <div className="text-sm font-orbitron text-blue-400">
            {chainDriftMinutes} min
          </div>
        </div>
      </div>

      {/* Export Button */}
      <div className="fixed bottom-4 right-4">
        <button
          onClick={handleExport}
          disabled={evidenceItems.length === 0}
          className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={t("evidenceChain.export") || "Export Evidence Chain JSON"}
        >
          {t("evidenceChain.export") || "Export Evidence Chain JSON"}
        </button>
      </div>
    </div>
  );
}






